import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { DeepKeys } from "@tanstack/react-table";
import {
  getMerchants,
  SettlementStatus,
  type MerchantListItemDto,
  type SortDirection,
  type MerchantViewMode,
} from "Infrastructure/Api/Api";
import { MS_TIME } from "Utils/TimeUtils";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  asEnumValue,
  asInt,
  asStartOfDayISO,
  asString,
  asNonEmptyStringArray,
} from "Utils/FilterUtils";

const MERCHANTS_LIST_QUERY_KEY = "MERCHANTS_LIST";

export type MerchantsListParams = {
  ownOnly?: boolean;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  statuses?: Array<string>;
  registrationSource?: string;
  licensePublicID?: string;
  viewMode?: MerchantViewMode;
  companyName?: string;
  companyNumber?: string;
  licenseOwnerName?: string;
  licenseNumber?: string;
  licenseCodes?: Array<string>;
  dateCreatedFrom?: string;
  dateCreatedTo?: string;
  phone?: string;
  email?: string;
  transactionsCountFrom?: number;
  settlementStatus?: SettlementStatus;
};

export const merchantsListKeys = {
  all: [MERCHANTS_LIST_QUERY_KEY],
  byParams: (params: MerchantsListParams) => [...merchantsListKeys.all, params],
};

// Column ids of the merchant tables — MRT derives them from accessorKeys,
// so they are dotted paths into MerchantListItemDto.
type MerchantColumnId = DeepKeys<MerchantListItemDto>;

// Keep in sync with MerchantsListFilterRequest.AllowedSortColumns on BE.
// Keys are MRT column ids, values are entity paths on Merchant (case-insensitive).
const SORT_COLUMN_MAP: Partial<Record<MerchantColumnId, string>> = {
  merchantStatus: "Status",
  "party.companyName": "Party.CompanyName",
  "party.companyNumber": "Party.CompanyNumber",
  "license.companyName": "PartyLicense.Party.CompanyName",
  "license.code": "PartyLicense.License.Code",
  "license.licenseNo": "PartyLicense.LicenseNo",
  dateCreated: "DateCreated",
  transactionsCount: "TransactionsCount",
};

export const mapMerchantsSortColumn = (
  sortBy?: MerchantColumnId,
): string | undefined =>
  sortBy ? (SORT_COLUMN_MAP[sortBy] ?? sortBy) : undefined;

export const mapMerchantsFiltersToParams = (
  filters: Partial<Record<MerchantColumnId, unknown>>,
): Partial<MerchantsListParams> => {
  const params: Partial<MerchantsListParams> = {
    statuses: asNonEmptyStringArray(filters.merchantStatus),
    companyName: asString(filters["party.companyName"]),
    companyNumber: asString(filters["party.companyNumber"]),
    licenseOwnerName: asString(filters["license.companyName"]),
    licenseNumber: asString(filters["license.licenseNo"]),
    licenseCodes: asNonEmptyStringArray(filters["license.code"]),
    dateCreatedFrom: asStartOfDayISO(filters.dateCreated),
    transactionsCountFrom: asInt(filters.transactionsCount),
    phone: asString(filters["party.phone"]),
    email: asString(filters["party.email"]),
    settlementStatus: asEnumValue(
      SettlementStatus,
      filters["party.settlementStatus"],
    ),
  };

  // Drop inactive filters so the spread at call sites cannot overwrite
  // page-fixed params (e.g. statuses) with undefined.
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as Partial<MerchantsListParams>;
};

export const useMerchantsListQuery = (params: MerchantsListParams = {}) =>
  useQuery({
    queryKey: merchantsListKeys.byParams(params),
    queryFn: processResponseWithApiProblemDetails(() =>
      getMerchants(
        params.ownOnly,
        params.statuses,
        params.registrationSource,
        params.licensePublicID,
        params.viewMode,
        params.companyName,
        params.companyNumber,
        params.licenseOwnerName,
        params.licenseNumber,
        params.licenseCodes,
        params.dateCreatedFrom,
        params.dateCreatedTo,
        params.phone,
        params.email,
        params.transactionsCountFrom,
        params.settlementStatus,
        params.offset,
        params.limit,
        params.sortBy,
        params.sortDirection,
      ),
    ),
    placeholderData: keepPreviousData,
    staleTime: 2 * MS_TIME.minute,
  });
